import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Calendar, Edit2, Save, X, Heart, Hash, Star,
  Sparkles, TrendingUp, Music, Zap, Info, Globe, BookOpen,
  Target, Users, Briefcase, Palette, ChevronDown, ChevronUp,
  MapPin, Clock, Compass
} from 'lucide-react';
import { useSpiritualStore } from '../hooks/useSpiritualStore';
import { useSacredAudio } from '../hooks/useSacredAudio';
import toast from 'react-hot-toast';
import PersonalCycles from './PersonalCycles';
import {
  getArchetype,
  getArchetypeWithCustomTerms,
  getCulturalArchetypes,
  getCompatibleNumbers,
  getChallengingNumbers,
  getRelationshipDynamic,
  type NumberArchetype,
  type CulturalArchetypeMapping
} from '../domains/numerology/archetypes';
import { calculateCompleteNatalChart } from '../domains/astrology/natal-chart';
import { ZODIAC_SIGNS, formatAstrologyDegree, getPlanetInfo, getZodiacColor, getHouseColor } from '../domains/astrology/calculator';
import { getPlanetInSignInterpretation, getAspectInterpretation, getHouseInterpretation } from '../domains/astrology/interpretations';
import { geocodeCity, reverseGeocode, getCurrentLocationWithCity, searchCities, type LocationData } from '../utils/geocoding';

/**
 * Archetype Card Component
 * Displays comprehensive archetype information with expandable details
 */
interface ArchetypeCardProps {
  number: number;
  archetype: NumberArchetype;
  label: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
}

const ArchetypeCard: React.FC<ArchetypeCardProps> = ({
  number,
  archetype,
  label,
  icon,
  isExpanded,
  onToggle
}) => {
  const culturalArchetypes = getCulturalArchetypes(number);
  const compatibleNumbers = getCompatibleNumbers(number);
  const challengingNumbers = getChallengingNumbers(number);

  return (
    <div className="archetype-card">
      <div className="archetype-header" onClick={onToggle}>
        <div className="archetype-icon">{icon}</div>
        <div className="archetype-content">
          <span className="archetype-label">{label}</span>
          <span className="archetype-value">{number}{archetype.isMaster && ' (Master)'}</span>
          <span className="archetype-title">{archetype.title}</span>
          <p className="archetype-essence">{archetype.essence}</p>
        </div>
        <button className="expand-button">
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="archetype-details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="archetype-details-content">
              {/* Core Meanings */}
              <div className="detail-section">
                <h4><BookOpen size={16} /> Core Meanings</h4>
                <div className="meaning-item">
                  <strong>Core:</strong> <p>{archetype.coreMeaning}</p>
                </div>
                <div className="meaning-item">
                  <strong>Symbolic:</strong> <p>{archetype.symbolicMeaning}</p>
                </div>
                <div className="meaning-item">
                  <strong>Spiritual:</strong> <p>{archetype.spiritualMeaning}</p>
                </div>
              </div>

              {/* Life Purpose & Lessons */}
              <div className="detail-section">
                <h4><Target size={16} /> Life Purpose & Lessons</h4>
                <div className="meaning-item">
                  <strong>Life Purpose:</strong> <p>{archetype.lifePurpose}</p>
                </div>
                {archetype.lifeLessons.length > 0 && (
                  <div className="meaning-item">
                    <strong>Life Lessons:</strong>
                    <ul>
                      {archetype.lifeLessons.map((lesson, idx) => (
                        <li key={idx}>{lesson}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Strengths & Challenges */}
              <div className="detail-section">
                <h4><TrendingUp size={16} /> Strengths & Challenges</h4>
                <div className="traits-grid">
                  <div className="trait-column">
                    <strong className="trait-label positive">Strengths</strong>
                    <ul>
                      {archetype.strengths.map((strength, idx) => (
                        <li key={idx}>{strength}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="trait-column">
                    <strong className="trait-label negative">Challenges</strong>
                    <ul>
                      {archetype.challenges.map((challenge, idx) => (
                        <li key={idx}>{challenge}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Talents & Shadow */}
              <div className="detail-section">
                <h4><Sparkles size={16} /> Talents & Shadow Aspects</h4>
                <div className="traits-grid">
                  <div className="trait-column">
                    <strong className="trait-label positive">Talents</strong>
                    <ul>
                      {archetype.talents.map((talent, idx) => (
                        <li key={idx}>{talent}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="trait-column">
                    <strong className="trait-label negative">Shadow Aspects</strong>
                    <ul>
                      {archetype.shadowAspects.map((shadow, idx) => (
                        <li key={idx}>{shadow}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Sacred Connections */}
              <div className="detail-section">
                <h4><Star size={16} /> Sacred Connections</h4>
                <div className="sacred-grid">
                  <div className="sacred-item">
                    <strong>Color:</strong> <span>{archetype.color}</span>
                  </div>
                  <div className="sacred-item">
                    <strong>Element:</strong> <span>{archetype.element}</span>
                  </div>
                  {archetype.planet && (
                    <div className="sacred-item">
                      <strong>Planet:</strong> <span>{archetype.planet}</span>
                    </div>
                  )}
                  {archetype.chakra && (
                    <div className="sacred-item">
                      <strong>Chakra:</strong> <span>{archetype.chakra}</span>
                    </div>
                  )}
                  <div className="sacred-item">
                    <strong>Frequency:</strong> <span>{archetype.frequency} Hz</span>
                  </div>
                </div>
              </div>

              {/* Symbolic Associations */}
              {archetype.symbols.length > 0 && (
                <div className="detail-section">
                  <h4><Palette size={16} /> Symbolic Associations</h4>
                  <div className="tags-list">
                    {archetype.symbols.map((symbol, idx) => (
                      <span key={idx} className="tag">{symbol}</span>
                    ))}
                  </div>
                  
                  {/* Shape Definitions */}
                  {archetype.shapeDefinitions && Object.keys(archetype.shapeDefinitions).length > 0 && (
                    <div className="meaning-item" style={{ marginTop: '1.5rem' }}>
                      <strong style={{ marginBottom: '1rem', display: 'block' }}>Shape Definitions & Practical Application:</strong>
                      <div className="shape-definitions-list">
                        {Object.entries(archetype.shapeDefinitions).map(([shapeName, definition]) => (
                          <div key={shapeName} className="shape-definition-item">
                            <div className="shape-name">
                              <strong>{shapeName}</strong>
                              <span className="shape-geometric-form">{definition.geometricForm}</span>
                            </div>
                            <p className="shape-description">{definition.description}</p>
                            <p className="shape-significance">{definition.significance}</p>
                            
                            {definition.practicalApplication && (
                              <div className="shape-practical-section">
                                <strong className="shape-section-title">How to Apply This to Your Life:</strong>
                                <p className="shape-practical-application">{definition.practicalApplication}</p>
                                
                                {definition.dailyLifeExamples && definition.dailyLifeExamples.length > 0 && (
                                  <div className="shape-examples-section">
                                    <strong className="shape-section-title">Daily Life Examples:</strong>
                                    <ul className="shape-examples-list">
                                      {definition.dailyLifeExamples.map((example, idx) => (
                                        <li key={idx}>{example}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                
                                {definition.howToWorkWithIt && definition.howToWorkWithIt.length > 0 && (
                                  <div className="shape-actions-section">
                                    <strong className="shape-section-title">How to Work With This Shape:</strong>
                                    <ul className="shape-actions-list">
                                      {definition.howToWorkWithIt.map((action, idx) => (
                                        <li key={idx}>{action}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {archetype.mythologicalFigures && archetype.mythologicalFigures.length > 0 && (
                    <div className="meaning-item">
                      <strong>Mythological Figures:</strong>
                      <div className="tags-list">
                        {archetype.mythologicalFigures.map((figure, idx) => (
                          <span key={idx} className="tag">{figure}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {archetype.gemstones && archetype.gemstones.length > 0 && (
                    <div className="meaning-item">
                      <strong>Gemstones:</strong>
                      <div className="tags-list">
                        {archetype.gemstones.map((stone, idx) => (
                          <span key={idx} className="tag">{stone}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Career Paths */}
              {archetype.careerPaths.length > 0 && (
                <div className="detail-section">
                  <h4><Briefcase size={16} /> Career Paths</h4>
                  <div className="tags-list">
                    {archetype.careerPaths.map((path, idx) => (
                      <span key={idx} className="tag">{path}</span>
                    ))}
                  </div>
                  <div className="meaning-item">
                    <strong>Vocational Calling:</strong> <p>{archetype.vocationalCalling}</p>
                  </div>
                </div>
              )}

              {/* Relationship Dynamics */}
              <div className="detail-section">
                <h4><Users size={16} /> Relationship Dynamics</h4>
                <div className="relationship-grid">
                  <div className="relationship-column">
                    <strong className="trait-label positive">Compatible Numbers</strong>
                    <div className="tags-list">
                      {compatibleNumbers.map((num) => (
                        <span key={num} className="tag positive">{num}</span>
                      ))}
                    </div>
                  </div>
                  <div className="relationship-column">
                    <strong className="trait-label negative">Challenging Numbers</strong>
                    <div className="tags-list">
                      {challengingNumbers.map((num) => (
                        <span key={num} className="tag negative">{num}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Cultural Archetype Mappings */}
              {culturalArchetypes.length > 0 && (
                <div className="detail-section">
                  <h4><Globe size={16} /> Cultural Archetype Mappings</h4>
                  <div className="cultural-grid">
                    {culturalArchetypes.map((cultural, idx) => (
                      <div key={idx} className="cultural-item">
                        <div className="cultural-header">
                          <strong>{cultural.tradition}</strong>
                          {cultural.luckyStatus && (
                            <span className={`lucky-badge ${cultural.luckyStatus}`}>
                              {cultural.luckyStatus}
                            </span>
                          )}
                        </div>
                        <p className="cultural-meaning">{cultural.meaning}</p>
                        {cultural.symbolism.length > 0 && (
                          <div className="tags-list">
                            {cultural.symbolism.map((symbol, sIdx) => (
                              <span key={sIdx} className="tag small">{symbol}</span>
                            ))}
                          </div>
                        )}
                        {cultural.notes && (
                          <p className="cultural-notes">{cultural.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Personal Profile Component
 * 
 * Complete personal numerology profile input and display
 * Includes:
 * - Birth date input
 * - Birth name input  
 * - Current name (if different)
 * - Life Path calculation
 * - Expression Number
 * - Personal Year/Month/Day cycles
 * - Personal frequencies
 * - Full archetypal numerology system with cultural mappings
 */
const PersonalProfile: React.FC = () => {
  const { user, setPersonalInfo } = useSpiritualStore();
  const { playSacredTone, isInitialized, initializeAudio } = useSacredAudio();
  const ASTRO_STORAGE_KEY = 'fr00t_astrological_data';

  type AstroStorageData = {
    birthTime: string;
    birthCity: string;
    birthLatitude: string;
    birthLongitude: string;
  };

  const readAstroData = (): AstroStorageData => {
    const empty: AstroStorageData = {
      birthTime: '',
      birthCity: '',
      birthLatitude: '',
      birthLongitude: ''
    };

    try {
      const sessionRaw = sessionStorage.getItem(ASTRO_STORAGE_KEY);
      const localRaw = localStorage.getItem(ASTRO_STORAGE_KEY);
      const sessionData = sessionRaw ? JSON.parse(sessionRaw) : {};
      const localData = localRaw ? JSON.parse(localRaw) : {};

      // Session data is the active source for current browsing session.
      return {
        ...empty,
        ...localData,
        ...sessionData
      };
    } catch (error) {
      console.error('Error reading astrological storage:', error);
      return empty;
    }
  };

  const writeAstroData = (data: AstroStorageData): boolean => {
    try {
      const serialized = JSON.stringify(data);
      sessionStorage.setItem(ASTRO_STORAGE_KEY, serialized);
      localStorage.setItem(ASTRO_STORAGE_KEY, serialized);
      return true;
    } catch (error) {
      console.error('Failed to persist astrological storage:', error);
      return false;
    }
  };
  
  const [isEditing, setIsEditing] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState<LocationData[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Initialize formData with localStorage data if available (for immediate access)
  const getInitialFormData = () => {
    try {
      const astroData = readAstroData();
      return {
        birthDate: '',
        birthTime: astroData.birthTime || '',
        birthName: '',
        currentName: '',
        birthCity: astroData.birthCity || '',
        birthLatitude: astroData.birthLatitude || '',
        birthLongitude: astroData.birthLongitude || ''
      };
    } catch (error) {
      console.error('Error loading initial form data from localStorage:', error);
      return {
        birthDate: '',
        birthTime: '',
        birthName: '',
        currentName: '',
        birthCity: '',
        birthLatitude: '',
        birthLongitude: ''
      };
    }
  };
  
  const [formData, setFormData] = useState(getInitialFormData);

  // Load user data and merge with localStorage astrological data
  useEffect(() => {
    if (user) {
      // Load astrological data from session/local storage
      const astroData = readAstroData();
      
      // Merge user data with localStorage data (localStorage takes precedence for astrological fields)
      setFormData({
        birthDate: user.birthDate || '',
        birthTime: astroData.birthTime || (user as any).birthTime || '',
        birthName: user.birthName || '',
        currentName: user.currentName || user.username || '',
        birthCity: astroData.birthCity || (user as any).birthCity || '',
        birthLatitude: astroData.birthLatitude || (user as any).birthLatitude || '',
        birthLongitude: astroData.birthLongitude || (user as any).birthLongitude || ''
      });
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('📋 Loaded form data:', {
          fromUser: {
            birthDate: user.birthDate,
            birthTime: (user as any).birthTime,
            birthCity: (user as any).birthCity
          },
          fromLocalStorage: astroData,
          merged: {
            birthTime: astroData.birthTime || (user as any).birthTime || '',
            birthCity: astroData.birthCity || (user as any).birthCity || '',
            birthLatitude: astroData.birthLatitude || (user as any).birthLatitude || '',
            birthLongitude: astroData.birthLongitude || (user as any).birthLongitude || ''
          }
        });
      }
    }
  }, [user]);

  useEffect(() => {
    if (!isInitialized) {
      initializeAudio();
    }
  }, [isInitialized, initializeAudio]);

  const handleSave = () => {
    if (!formData.birthDate || !formData.birthName) {
      toast.error('Please provide both birth date and birth name');
      return;
    }

    setPersonalInfo(formData.birthDate, formData.birthName, formData.currentName || formData.birthName);
    
    // Always store astrological data in session storage with local backup
    // Save all fields, even if empty (to preserve user's intent to clear fields)
    const astroData = {
      birthTime: formData.birthTime || '',
      birthCity: formData.birthCity || '',
      birthLatitude: formData.birthLatitude || '',
      birthLongitude: formData.birthLongitude || ''
    };
    
    if (!writeAstroData(astroData)) {
      toast.error('Failed to save birth details. Please try again.');
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('💾 Saved astrological data:', astroData);
      console.log('✅ Verified saved data:', readAstroData());
    }
    
    setIsEditing(false);
    setAstrologyFocus(true);
    
    // Show success message with details
    const savedFields = [];
    if (formData.birthTime) savedFields.push('birth time');
    if (formData.birthCity || (formData.birthLatitude && formData.birthLongitude)) savedFields.push('birth location');
    
    if (savedFields.length > 0) {
      toast.success(`✨ Personal profile updated! Saved: ${savedFields.join(', ')}`);
    } else {
      toast.success('✨ Personal profile updated!');
    }
  };

  const handleCancel = () => {
    if (user) {
      const astroData = readAstroData();
      setFormData({
        birthDate: user.birthDate || '',
        birthTime: astroData.birthTime || '',
        birthName: user.birthName || '',
        currentName: user.currentName || '',
        birthCity: astroData.birthCity || '',
        birthLatitude: astroData.birthLatitude || '',
        birthLongitude: astroData.birthLongitude || ''
      });
    }
    setIsEditing(false);
  };

  const playPersonalFrequency = (frequency: number, label: string) => {
    if (!isInitialized) {
      toast.error('Audio system initializing...');
      return;
    }
    playSacredTone(frequency, 2.0);
    toast.success(`🎵 Playing ${label} frequency: ${frequency} Hz`);
  };

  const profile = user?.numerology?.personalProfile;
  const hasPersonalData = user?.birthDate && user?.birthName;

  // Get full archetype data for each core number
  const lifePathArchetype = profile ? getArchetypeWithCustomTerms(profile.lifePath.lifePathNumber) : null;
  const expressionArchetype = profile ? getArchetypeWithCustomTerms(profile.birthNameProfile.expressionNumber) : null;
  const soulUrgeArchetype = profile ? getArchetypeWithCustomTerms(profile.birthNameProfile.soulUrgeNumber) : null;
  const personalityArchetype = profile ? getArchetypeWithCustomTerms(profile.birthNameProfile.personalityNumber) : null;

  // State for expanded sections
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    lifePath: false,
    expression: false,
    soulUrge: false,
    personality: false
  });
  const [astrologyFocus, setAstrologyFocus] = useState<boolean>(true);

  useEffect(() => {
    if (hasPersonalData && !isEditing) {
      setAstrologyFocus(true);
    }
  }, [hasPersonalData, isEditing]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // State for natal chart calculation (now async)
  const [natalChart, setNatalChart] = useState<any>(null);
  const [isCalculatingChart, setIsCalculatingChart] = useState(false);

  // Calculate natal chart asynchronously using astronomy-calculations.js
  useEffect(() => {
    if (!user?.birthDate) {
      setNatalChart(null);
      return;
    }
    
    let isCancelled = false;
    setIsCalculatingChart(true);
    
    (async () => {
      try {
        // Prefer formData values (current state) over localStorage (saved state)
        // This ensures the chart updates immediately when user enters data
        const astroData = readAstroData();
        
        // Use formData if available, otherwise fall back to localStorage
        const birthTime = formData.birthTime || astroData.birthTime || undefined;
        const birthLatitudeStr = formData.birthLatitude || astroData.birthLatitude;
        const birthLongitudeStr = formData.birthLongitude || astroData.birthLongitude;
        const birthCity = formData.birthCity || astroData.birthCity;
        
        // Validate and parse location data
        let birthLocation: { latitude: number; longitude: number; city?: string } | undefined = undefined;
        
        if (birthLatitudeStr && birthLongitudeStr) {
          const lat = parseFloat(birthLatitudeStr);
          const lon = parseFloat(birthLongitudeStr);
          
          // Check if parsing was successful and values are valid
          if (!isNaN(lat) && !isNaN(lon) && 
              lat >= -90 && lat <= 90 && 
              lon >= -180 && lon <= 180) {
            birthLocation = {
              latitude: lat,
              longitude: lon,
              city: birthCity
            };
          } else {
            console.warn('Invalid birth location coordinates:', { 
              lat, 
              lon, 
              latStr: birthLatitudeStr, 
              lonStr: birthLongitudeStr 
            });
          }
        }
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('🌍 Calculating natal chart with:', {
            birthDate: user.birthDate,
            birthTime: birthTime || 'not provided',
            birthLocation: birthLocation || 'not provided',
            formData: {
              birthLatitude: formData.birthLatitude,
              birthLongitude: formData.birthLongitude,
              birthTime: formData.birthTime
            },
            astroData
          });
        }
        
        // Calculate using astronomy-calculations.js (async)
        if (!user.birthDate) {
          console.warn('No birth date available for calculation');
          if (!isCancelled) {
            setNatalChart(null);
            setIsCalculatingChart(false);
          }
          return;
        }
        
        const chart = await calculateCompleteNatalChart(
          user.birthDate,
          birthTime,
          birthLocation
        );
        
        // Only update if this calculation wasn't cancelled
        if (!isCancelled) {
          // Debug: Log that we're using the new calculations
          if (process.env.NODE_ENV === 'development') {
            console.log('🌍 Natal chart calculated with astronomy-calculations.js:', {
              sun: `${chart.planets.find(p => p.planet === 'Sun')?.sign} ${formatAstrologyDegree(chart.planets.find(p => p.planet === 'Sun')?.degree || 0)}`,
              moon: `${chart.planets.find(p => p.planet === 'Moon')?.sign} ${formatAstrologyDegree(chart.planets.find(p => p.planet === 'Moon')?.degree || 0)}`,
              ascendant: chart.ascendant ? `${chart.ascendant.sign} ${formatAstrologyDegree(chart.ascendant.degree)}` : 'N/A (no location)',
              hasLocation: !!birthLocation,
              ascendantLongitude: chart.ascendant?.longitude
            });
          }
          
          setNatalChart(chart);
          setIsCalculatingChart(false);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error calculating natal chart:', error);
          setNatalChart(null);
          setIsCalculatingChart(false);
        }
      }
    })();
    
    // Cleanup function to cancel stale calculations
    return () => {
      isCancelled = true;
    };
  }, [user?.birthDate, formData.birthTime, formData.birthLatitude, formData.birthLongitude, formData.birthCity]);

  // Ensure astrological data persists - this runs after initial load to catch any missed data
  useEffect(() => {
    if (user?.birthDate) {
      const astroData = readAstroData();
      
      // Only update if localStorage has data that's not in formData
      // This prevents overwriting user input with stale localStorage data
      if (astroData.birthTime || astroData.birthCity || astroData.birthLatitude || astroData.birthLongitude) {
        setFormData(prev => {
          // Only update fields that are empty in formData but exist in localStorage
          const updates: Partial<typeof prev> = {};
          if (!prev.birthTime && astroData.birthTime) updates.birthTime = astroData.birthTime;
          if (!prev.birthCity && astroData.birthCity) updates.birthCity = astroData.birthCity;
          if (!prev.birthLatitude && astroData.birthLatitude) updates.birthLatitude = astroData.birthLatitude;
          if (!prev.birthLongitude && astroData.birthLongitude) updates.birthLongitude = astroData.birthLongitude;
          
          // Only update if there are actual changes
          if (Object.keys(updates).length > 0) {
            if (process.env.NODE_ENV === 'development') {
              console.log('📍 Filling missing astrological data from localStorage:', updates);
            }
            return { ...prev, ...updates };
          }
          return prev;
        });
      }
    }
  }, [user?.birthDate]);

  // Search for city suggestions as user types (with debounce)
  useEffect(() => {
    if (!formData.birthCity || formData.birthCity.trim().length < 2) {
      setCitySuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Don't show suggestions if coordinates are already set (user already selected)
    if (formData.birthLatitude && formData.birthLongitude) {
      setCitySuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const suggestions = await searchCities(formData.birthCity, 5);
        setCitySuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
        setSelectedSuggestionIndex(-1);
      } catch (error) {
        console.error('City search error:', error);
        setCitySuggestions([]);
        setShowSuggestions(false);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [formData.birthCity, formData.birthLatitude, formData.birthLongitude]);

  // Auto-geocode when city is entered (with debounce) - only if no suggestions selected
  useEffect(() => {
    if (!formData.birthCity || formData.birthCity.trim().length === 0) {
      return;
    }

    // Only geocode if coordinates are not already set and no suggestions are showing
    if (formData.birthLatitude && formData.birthLongitude) {
      return;
    }

    // Don't auto-geocode if user is actively selecting from suggestions
    if (showSuggestions && citySuggestions.length > 0) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsGeocoding(true);
      try {
        const location = await geocodeCity(formData.birthCity);
        if (location) {
          setFormData(prev => ({
            ...prev,
            birthLatitude: location.latitude.toString(),
            birthLongitude: location.longitude.toString(),
            birthCity: location.city // Use the standardized city name
          }));
          toast.success(`📍 Found ${location.city} (${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)})`);
          setShowSuggestions(false);
          setCitySuggestions([]);
        }
      } catch (error) {
        console.error('Geocoding error:', error);
      } finally {
        setIsGeocoding(false);
      }
    }, 1500); // Wait longer for auto-geocode (1.5 seconds)

    return () => clearTimeout(timeoutId);
  }, [formData.birthCity, showSuggestions, citySuggestions.length]);

  // Auto-reverse geocode when coordinates are entered
  useEffect(() => {
    if (!formData.birthLatitude || !formData.birthLongitude) {
      return;
    }

    const lat = parseFloat(formData.birthLatitude);
    const lon = parseFloat(formData.birthLongitude);

    if (isNaN(lat) || isNaN(lon)) {
      return;
    }

    // Only reverse geocode if city is not already set
    if (formData.birthCity && formData.birthCity.trim().length > 0) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsGeocoding(true);
      try {
        const location = await reverseGeocode(lat, lon);
        if (location) {
          setFormData(prev => ({
            ...prev,
            birthCity: location.city
          }));
          toast.success(`📍 Found ${location.city}`);
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      } finally {
        setIsGeocoding(false);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData.birthLatitude, formData.birthLongitude]);

  // Handle city selection from autocomplete
  const handleSelectCity = (location: LocationData) => {
    setFormData(prev => ({
      ...prev,
      birthCity: location.city,
      birthLatitude: location.latitude.toString(),
      birthLongitude: location.longitude.toString()
    }));
    setShowSuggestions(false);
    setCitySuggestions([]);
    setSelectedSuggestionIndex(-1);
    toast.success(`📍 Selected ${location.city} (${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)})`);
  };

  // Handle auto-detect current location
  const handleDetectLocation = async () => {
    setIsDetectingLocation(true);
    try {
      const location = await getCurrentLocationWithCity();
      if (location) {
        setFormData(prev => ({
          ...prev,
          birthCity: location.city,
          birthLatitude: location.latitude.toString(),
          birthLongitude: location.longitude.toString()
        }));
        setShowSuggestions(false);
        setCitySuggestions([]);
        toast.success(`📍 Detected location: ${location.city}`);
      } else {
        toast.error('Could not detect your location. Please enter manually.');
      }
    } catch (error: any) {
      console.error('Location detection error:', error);
      if (error.code === 1) {
        toast.error('Location access denied. Please enable location permissions.');
      } else {
        toast.error('Could not detect location. Please enter manually.');
      }
    } finally {
      setIsDetectingLocation(false);
    }
  };

  return (
    <div className="personal-profile-container">
      {/* Header */}
      <div className="profile-header">
        <div className="header-content">
          <User className="header-icon" size={32} />
          <div>
            <h2>Personal Profile</h2>
            <p>Your unique numerological and spiritual blueprint</p>
          </div>
        </div>
        {hasPersonalData && !isEditing && (
          <div className="profile-header-actions">
            <button
              className="view-toggle-button"
              onClick={() => setAstrologyFocus((prev) => !prev)}
              aria-label="Toggle astrology focus view"
            >
              {astrologyFocus ? 'View Full Profile' : 'Astrology Focus'}
            </button>
            <button
              className="edit-button"
              onClick={() => setIsEditing(true)}
              aria-label="Edit personal profile"
            >
              <Edit2 size={18} />
              <span>Edit</span>
            </button>
          </div>
        )}
      </div>

      {/* Edit Form */}
      {(isEditing || !hasPersonalData) && (
        <motion.div
          className="edit-form"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="form-grid">
            <div className="form-group">
              <label>
                <Calendar size={18} />
                <span>Birth Date</span>
              </label>
              <input
                className="birth-date-input"
                type="date"
                value={formData.birthDate}
                onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
              />
              <span className="field-hint">Your date of birth (required)</span>
            </div>

            <div className="form-group">
              <label>
                <User size={18} />
                <span>Birth Name</span>
              </label>
              <input
                type="text"
                value={formData.birthName}
                onChange={(e) => setFormData({ ...formData, birthName: e.target.value })}
                placeholder="Full name at birth"
              />
              <span className="field-hint">Your complete name as given at birth (required)</span>
            </div>

            <div className="form-group">
              <label>
                <Sparkles size={18} />
                <span>Current Name</span>
              </label>
              <input
                type="text"
                value={formData.currentName}
                onChange={(e) => setFormData({ ...formData, currentName: e.target.value })}
                placeholder="Current name (if different)"
              />
              <span className="field-hint">Leave blank if same as birth name</span>
            </div>

            <div className="form-group">
              <label>
                <Clock size={18} />
                <span>Birth Time (Optional)</span>
              </label>
              <input
                type="time"
                value={formData.birthTime}
                onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
              />
              <span className="field-hint">For accurate Rising sign and houses (HH:MM format)</span>
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>
                <MapPin size={18} />
                <span>Birth City {!formData.birthLatitude || !formData.birthLongitude ? <span style={{ color: '#f59e0b' }}>*</span> : ''}</span>
              </label>
              <div className="city-input-row" style={{ display: 'flex', gap: '0.5rem', position: 'relative' }}>
                <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                  <input
                    type="text"
                    value={formData.birthCity}
                    onChange={(e) => {
                      setFormData({ ...formData, birthCity: e.target.value });
                      setShowSuggestions(true);
                      setSelectedSuggestionIndex(-1);
                    }}
                    onFocus={() => {
                      if (citySuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={(e) => {
                      // Delay hiding suggestions to allow click on suggestion
                      setTimeout(() => {
                        // Check if focus moved to a suggestion or stayed in the input
                        const activeElement = document.activeElement;
                        const isClickingSuggestion = activeElement?.closest('[data-suggestion-list]');
                        if (!isClickingSuggestion) {
                          setShowSuggestions(false);
                        }
                      }, 200);
                    }}
                    onKeyDown={(e) => {
                      if (showSuggestions && citySuggestions.length > 0) {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setSelectedSuggestionIndex(prev => 
                            prev < citySuggestions.length - 1 ? prev + 1 : prev
                          );
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
                        } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
                          e.preventDefault();
                          handleSelectCity(citySuggestions[selectedSuggestionIndex]);
                        } else if (e.key === 'Escape') {
                          setShowSuggestions(false);
                          setSelectedSuggestionIndex(-1);
                        } else if (e.key === 'Tab' && selectedSuggestionIndex >= 0) {
                          e.preventDefault();
                          handleSelectCity(citySuggestions[selectedSuggestionIndex]);
                        }
                      }
                    }}
                    placeholder="City name"
                    style={{ width: '100%' }}
                  />
                  {/* Autocomplete suggestions dropdown */}
                  {showSuggestions && citySuggestions.length > 0 && (
                    <div 
                      data-suggestion-list
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        background: 'rgba(15, 23, 42, 0.98)',
                        border: '2px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: '8px',
                        marginTop: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      {citySuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => handleSelectCity(suggestion)}
                          onMouseDown={(e) => {
                            // Prevent input blur when clicking suggestion
                            e.preventDefault();
                            handleSelectCity(suggestion);
                          }}
                          onMouseEnter={() => setSelectedSuggestionIndex(index)}
                          style={{
                            padding: '0.75rem 1rem',
                            cursor: 'pointer',
                            background: selectedSuggestionIndex === index 
                              ? 'rgba(139, 92, 246, 0.2)' 
                              : 'transparent',
                            borderBottom: index < citySuggestions.length - 1 
                              ? '1px solid rgba(139, 92, 246, 0.1)' 
                              : 'none',
                            transition: 'background 0.2s'
                          }}
                        >
                          <div style={{ 
                            fontWeight: 500, 
                            color: '#f1f5f9',
                            marginBottom: '0.25rem'
                          }}>
                            {suggestion.city}
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#94a3b8' 
                          }}>
                            {[suggestion.state, suggestion.country].filter(Boolean).join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={isDetectingLocation}
                  className="detect-location-button"
                  title="Use current location"
                >
                  {isDetectingLocation ? '...' : '📍'}
                </button>
              </div>
              <span className="field-hint">
                {isGeocoding ? 'Looking up location...' : 
                 (!formData.birthLatitude || !formData.birthLongitude) 
                   ? '⚠️ Required for Rising Sign calculation (auto-fills coordinates)' 
                   : 'City where you were born (auto-fills coordinates)'}
              </span>
            </div>

            <div className="form-group">
              <label>
                <Compass size={18} />
                <span>Birth Coordinates {!formData.birthLatitude || !formData.birthLongitude ? <span style={{ color: '#f59e0b' }}>*</span> : ''}</span>
              </label>
              <div className="coordinates-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.5rem' }}>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.birthLatitude}
                  onChange={(e) => setFormData({ ...formData, birthLatitude: e.target.value })}
                  placeholder="Latitude"
                />
                <input
                  type="number"
                  step="0.0001"
                  value={formData.birthLongitude}
                  onChange={(e) => setFormData({ ...formData, birthLongitude: e.target.value })}
                  placeholder="Longitude"
                />
              </div>
              <span className="field-hint">
                {isGeocoding ? 'Looking up city...' : 
                 (!formData.birthLatitude || !formData.birthLongitude)
                   ? '⚠️ Required for Rising Sign and house calculations (auto-filled from city above)'
                   : 'Coordinates for accurate Ascendant and houses (auto-filled from city)'}
              </span>
            </div>
          </div>

          <div className="form-actions">
            <button
              className="save-button"
              onClick={handleSave}
              aria-label="Save personal profile"
            >
              <Save size={18} />
              <span>Save Profile</span>
            </button>
            {hasPersonalData && (
              <button
                className="cancel-button"
                onClick={handleCancel}
                aria-label="Cancel editing personal profile"
              >
                <X size={18} />
                <span>Cancel</span>
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Profile Display */}
      {hasPersonalData && profile && !isEditing && (
        <div className="profile-content">
          {!astrologyFocus && (
            <>
          {/* Core Numbers - Enhanced with Full Archetype System */}
          <motion.div
            className="section core-numbers"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h3>Core Numbers</h3>
            <div className="numbers-grid">
              {/* Life Path */}
              {lifePathArchetype && (
                <ArchetypeCard
                  number={profile.lifePath.lifePathNumber}
                  archetype={lifePathArchetype}
                  label="Life Path"
                  icon={<TrendingUp />}
                  isExpanded={expandedSections.lifePath}
                  onToggle={() => toggleSection('lifePath')}
                />
              )}

              {/* Expression */}
              {expressionArchetype && (
                <ArchetypeCard
                  number={profile.birthNameProfile.expressionNumber}
                  archetype={expressionArchetype}
                  label="Expression"
                  icon={<Star />}
                  isExpanded={expandedSections.expression}
                  onToggle={() => toggleSection('expression')}
                />
              )}

              {/* Soul Urge */}
              {soulUrgeArchetype && (
                <ArchetypeCard
                  number={profile.birthNameProfile.soulUrgeNumber}
                  archetype={soulUrgeArchetype}
                  label="Soul Urge"
                  icon={<Heart />}
                  isExpanded={expandedSections.soulUrge}
                  onToggle={() => toggleSection('soulUrge')}
                />
              )}

              {/* Personality */}
              {personalityArchetype && (
                <ArchetypeCard
                  number={profile.birthNameProfile.personalityNumber}
                  archetype={personalityArchetype}
                  label="Personality"
                  icon={<Sparkles />}
                  isExpanded={expandedSections.personality}
                  onToggle={() => toggleSection('personality')}
                />
              )}
            </div>
          </motion.div>

          {/* Personal Frequencies */}
          <motion.div
            className="section frequencies"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3>
              <Music size={24} />
              Personal Frequencies
            </h3>
            <div className="frequencies-grid">
              <div 
                className="frequency-card"
                onClick={() => playPersonalFrequency(profile.primaryFrequency, 'Primary')}
              >
                <Zap className="freq-icon" />
                <div className="freq-content">
                  <span className="freq-label">Primary Frequency</span>
                  <span className="freq-value">{profile.primaryFrequency} Hz</span>
                  <span className="freq-description">Based on Life Path</span>
                </div>
                <button className="play-button">▶</button>
              </div>

              <div 
                className="frequency-card"
                onClick={() => playPersonalFrequency(profile.secondaryFrequency, 'Secondary')}
              >
                <Zap className="freq-icon" />
                <div className="freq-content">
                  <span className="freq-label">Secondary Frequency</span>
                  <span className="freq-value">{profile.secondaryFrequency} Hz</span>
                  <span className="freq-description">Based on Expression</span>
                </div>
                <button className="play-button">▶</button>
              </div>

              <div 
                className="frequency-card"
                onClick={() => playPersonalFrequency(profile.experimentalFrequency, 'Healing')}
              >
                <Zap className="freq-icon" />
                <div className="freq-content">
                  <span className="freq-label">Healing Frequency</span>
                  <span className="freq-value">{profile.experimentalFrequency} Hz</span>
                  <span className="freq-description">For current year cycle</span>
                </div>
                <button className="play-button">▶</button>
              </div>
            </div>
          </motion.div>

          {/* Current Cycles */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <PersonalCycles numerologyProfile={profile} embedded />
          </motion.div>

          {/* Summary */}
          <motion.div
            className="section summary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3>
              <Info size={24} />
              Your Spiritual Blueprint
            </h3>
            <div className="summary-content">
              <div className="summary-item">
                <strong>Core Identity:</strong>
                <p>{profile.summary.coreIdentity}</p>
              </div>
              <div className="summary-item">
                <strong>Current Phase:</strong>
                <p>{profile.summary.currentPhase}</p>
              </div>
              <div className="summary-item">
                <strong>Key Strengths:</strong>
                <ul>
                  {profile.summary.keyStrengths.map((strength: string, idx: number) => (
                    <li key={idx}>{strength}</li>
                  ))}
                </ul>
              </div>
              <div className="summary-item">
                <strong>Growth Areas:</strong>
                <ul>
                  {profile.summary.mainChallenges.map((challenge: string, idx: number) => (
                    <li key={idx}>{challenge}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
            </>
          )}

          {/* Birth Details Summary */}
          {hasPersonalData && !isEditing && (() => {
            // Get current astrological data for display
            const astroData = readAstroData();
            const displayTime = formData.birthTime || astroData.birthTime || '';
            const displayCity = formData.birthCity || astroData.birthCity || '';
            const displayLat = formData.birthLatitude || astroData.birthLatitude || '';
            const displayLon = formData.birthLongitude || astroData.birthLongitude || '';
            
            return (
              <motion.div
                className="section birth-details"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3>
                    <Calendar size={24} />
                    Birth Details
                  </h3>
                  <button
                    className="edit-button"
                    onClick={() => setIsEditing(true)}
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    aria-label="Edit birth details"
                  >
                    <Edit2 size={16} />
                    <span>Edit</span>
                  </button>
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', 
                  gap: '1rem',
                  background: 'rgba(139, 92, 246, 0.05)',
                  padding: '1rem',
                  borderRadius: '12px',
                  border: '1px solid rgba(139, 92, 246, 0.2)'
                }}>
                  <div>
                    <strong style={{ color: '#8b5cf6', fontSize: '0.875rem' }}>Birth Date</strong>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem' }}>
                      {user?.birthDate ? new Date(user.birthDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <strong style={{ color: '#8b5cf6', fontSize: '0.875rem' }}>Birth Time</strong>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem' }}>
                      {displayTime || 'Not provided'}
                    </p>
                    {!displayTime && (
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#f59e0b' }}>
                        ⚠️ Add time for accurate Rising Sign
                      </p>
                    )}
                  </div>
                  <div>
                    <strong style={{ color: '#8b5cf6', fontSize: '0.875rem' }}>Birth Location</strong>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '1rem' }}>
                      {displayCity || 'Not provided'}
                    </p>
                    {displayLat && displayLon ? (
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                        {parseFloat(displayLat).toFixed(4)}, {parseFloat(displayLon).toFixed(4)}
                      </p>
                    ) : (
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#f59e0b' }}>
                        ⚠️ Add location for Rising Sign
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })()}

          {/* Astrological Chart Section */}
          {natalChart && (
            <motion.div
              className="section astrological-chart"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3>
                <Star size={24} />
                Your Natal Chart
              </h3>
              
              {/* Big Three */}
              <div className="big-three">
                <div className="big-three-item">
                  <div className="big-three-label" style={{ color: getPlanetInfo('Sun').color }}>
                    <span style={{ fontSize: '1.3em', marginRight: '0.3rem' }}>{getPlanetInfo('Sun').symbol}</span>
                    Sun Sign
                  </div>
                  <div className="big-three-value" style={{ 
                    color: getZodiacColor(natalChart.planets.find((p: any) => p.planet === 'Sun')?.sign || 'Aries') 
                  }}>
                    {natalChart.planets.find((p: any) => p.planet === 'Sun')?.sign}{' '}
                    {formatAstrologyDegree(natalChart.planets.find((p: any) => p.planet === 'Sun')?.degree || 0)}
                  </div>
                  <div className="big-three-description">Your core identity and ego</div>
                </div>
                <div className="big-three-item">
                  <div className="big-three-label" style={{ color: getPlanetInfo('Moon').color }}>
                    <span style={{ fontSize: '1.3em', marginRight: '0.3rem' }}>{getPlanetInfo('Moon').symbol}</span>
                    Moon Sign
                  </div>
                  <div className="big-three-value" style={{ 
                    color: getZodiacColor(natalChart.planets.find((p: any) => p.planet === 'Moon')?.sign || 'Aries') 
                  }}>
                    {natalChart.planets.find((p: any) => p.planet === 'Moon')?.sign}{' '}
                    {formatAstrologyDegree(natalChart.planets.find((p: any) => p.planet === 'Moon')?.degree || 0)}
                  </div>
                  <div className="big-three-description">Your emotional nature</div>
                </div>
                {natalChart.ascendant ? (
                  <div className="big-three-item">
                    <div className="big-three-label" style={{ color: '#8B5CF6' }}>Rising Sign</div>
                    <div className="big-three-value" style={{ 
                      color: getZodiacColor(natalChart.ascendant.sign) 
                    }}>
                      {natalChart.ascendant.sign}{' '}
                      {formatAstrologyDegree(natalChart.ascendant.degree)}
                    </div>
                    <div className="big-three-description">Your outer personality</div>
                  </div>
                ) : (
                  <div className="big-three-item" style={{ 
                    opacity: 0.6, 
                    border: '2px dashed rgba(139, 92, 246, 0.3)',
                    background: 'rgba(139, 92, 246, 0.05)'
                  }}>
                    <div className="big-three-label">Rising Sign</div>
                    <div className="big-three-value" style={{ color: '#f59e0b' }}>
                      Not Calculated
                    </div>
                    <div className="big-three-description" style={{ fontSize: '0.85rem', color: '#f59e0b' }}>
                      ⚠️ Add birth location above to calculate your Rising Sign
                    </div>
                  </div>
                )}
              </div>

              {/* All Planetary Positions */}
              <div className="planets-grid">
                <h4>Planetary Positions</h4>
                <div className="planets-list">
                  {natalChart.planets.map((planet: any) => {
                    const signData = ZODIAC_SIGNS[planet.sign.toLowerCase() as keyof typeof ZODIAC_SIGNS];
                    const planetInfo = getPlanetInfo(planet.planet);
                    const zodiacColor = getZodiacColor(planet.sign);
                    const interpretation = getPlanetInSignInterpretation(planet.planet, planet.sign);
                    const houseInfo = planet.house ? getHouseInterpretation(planet.house) : null;
                    
                    return (
                      <div key={planet.planet} className="planet-item" style={{ borderLeftColor: planetInfo.color, borderLeftWidth: '3px' }}>
                        <div className="planet-header">
                          <span className="planet-name" style={{ color: planetInfo.color }}>
                            <span className="planet-glyph" style={{ fontSize: '1.2em', marginRight: '0.25rem' }}>{planetInfo.symbol}</span>
                            {planet.planet}
                          </span>
                          <span className="planet-sign" style={{ color: zodiacColor }}>{signData?.symbol} {planet.sign} {formatAstrologyDegree(planet.degree)}</span>
                        </div>
                        <div className="planet-details">
                          <span className="planet-element">{signData?.element}</span>
                          <span className="planet-modality">{signData?.modality}</span>
                          {planet.house && <span className="planet-house">House {planet.house}</span>}
                        </div>
                        <div className="planet-interpretation">
                          <p className="planet-meaning">{interpretation.meaning}</p>
                          {houseInfo && (
                            <p className="planet-house-meaning">In {houseInfo.meaning}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Elemental Balance */}
              <div className="elemental-balance">
                <h4>Elemental Balance</h4>
                <div className="elements-grid">
                  <div className="element-item">
                    <div className="element-label">Fire</div>
                    <div className="element-bar">
                      <div 
                        className="element-fill fire" 
                        style={{ width: `${(natalChart.elementalBalance.fire / 10) * 100}%` }}
                      />
                    </div>
                    <div className="element-count">{natalChart.elementalBalance.fire}</div>
                  </div>
                  <div className="element-item">
                    <div className="element-label">Earth</div>
                    <div className="element-bar">
                      <div 
                        className="element-fill earth" 
                        style={{ width: `${(natalChart.elementalBalance.earth / 10) * 100}%` }}
                      />
                    </div>
                    <div className="element-count">{natalChart.elementalBalance.earth}</div>
                  </div>
                  <div className="element-item">
                    <div className="element-label">Air</div>
                    <div className="element-bar">
                      <div 
                        className="element-fill air" 
                        style={{ width: `${(natalChart.elementalBalance.air / 10) * 100}%` }}
                      />
                    </div>
                    <div className="element-count">{natalChart.elementalBalance.air}</div>
                  </div>
                  <div className="element-item">
                    <div className="element-label">Water</div>
                    <div className="element-bar">
                      <div 
                        className="element-fill water" 
                        style={{ width: `${(natalChart.elementalBalance.water / 10) * 100}%` }}
                      />
                    </div>
                    <div className="element-count">{natalChart.elementalBalance.water}</div>
                  </div>
                </div>
              </div>

              {/* Major Aspects */}
              {natalChart.aspects.length > 0 && (
                <div className="aspects-section">
                  <h4>Major Aspects</h4>
                  <div className="aspects-list">
                    {natalChart.aspects.slice(0, 10).map((aspect: any, idx: number) => {
                      const interpretation = getAspectInterpretation(aspect);
                      return (
                        <div key={idx} className="aspect-item">
                          <div className="aspect-header">
                            <span className="aspect-planets">
                              {aspect.planet1} {aspect.type} {aspect.planet2}
                            </span>
                            <span className="aspect-details">
                              {aspect.angle}° (orb: {aspect.orb.toFixed(1)}°)
                            </span>
                          </div>
                          <p className="aspect-meaning">{interpretation.meaning}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Houses */}
              {natalChart.houses.length > 0 && (
                <div className="houses-section">
                  <h4>House Cusps</h4>
                  <div className="houses-grid">
                    {natalChart.houses.map((house: any) => {
                      const signData = ZODIAC_SIGNS[house.sign.toLowerCase() as keyof typeof ZODIAC_SIGNS];
                      const houseColor = getHouseColor(house.house);
                      const zodiacColor = getZodiacColor(house.sign);
                      const houseInfo = getHouseInterpretation(house.house);
                      return (
                        <div key={house.house} className="house-item" style={{ borderTopColor: houseColor, borderTopWidth: '3px' }}>
                          <div className="house-number" style={{ color: houseColor }}>House {house.house}</div>
                          <div className="house-sign" style={{ color: zodiacColor }}>{signData?.symbol} {house.sign} {formatAstrologyDegree(house.degree)}</div>
                          <div className="house-meaning">{houseInfo.meaning}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      )}

      <style>{`
        .personal-profile-container {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          margin: 0;
          padding: clamp(0.75rem, 1.8vw, 1.5rem);
          box-sizing: border-box;
          --pp-font-sans: var(--fr00t-font-primary, 'Poppins', sans-serif);
          --pp-font-mono: var(--fr00t-font-mono, 'Courier New', monospace);
          --pp-text-label: 0.8rem;
          --pp-text-meta: 0.85rem;
          --pp-text-body: 0.9rem;
          --pp-tracking: 0.05em;
          font-family: var(--pp-font-sans);
        }

        .profile-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 2px solid rgba(139, 92, 246, 0.3);
        }

        .profile-header-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .view-toggle-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: rgba(59, 130, 246, 0.18);
          border: 1px solid rgba(59, 130, 246, 0.45);
          border-radius: 8px;
          color: #dbeafe;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .view-toggle-button:hover {
          background: rgba(59, 130, 246, 0.28);
          transform: translateY(-2px);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          color: #8b5cf6;
        }

        .profile-header h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .profile-header p {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.95rem;
        }

        .edit-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.5);
          border-radius: 8px;
          color: #ffffff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .edit-button:hover {
          background: rgba(139, 92, 246, 0.3);
          transform: translateY(-2px);
        }

        .edit-form {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          box-sizing: border-box;
          padding: clamp(1rem, 1.8vw, 1.5rem);
          margin-bottom: 2rem;
        }

        .form-grid {
          display: grid;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          min-width: 0;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          min-width: 0;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #ffffff;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .form-group input {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          box-sizing: border-box;
          padding: 0.75rem 1rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 8px;
          color: #ffffff;
          font-size: 1rem;
        }

        .birth-date-input {
          font-size: clamp(0.78rem, 2.1vw, 1rem);
          font-variant-numeric: tabular-nums;
        }

        .birth-date-input::-webkit-datetime-edit,
        .birth-date-input::-webkit-datetime-edit-fields-wrapper,
        .birth-date-input::-webkit-datetime-edit-text,
        .birth-date-input::-webkit-datetime-edit-month-field,
        .birth-date-input::-webkit-datetime-edit-day-field,
        .birth-date-input::-webkit-datetime-edit-year-field {
          font-size: inherit;
          line-height: 1.2;
        }

        .birth-date-input::-webkit-calendar-picker-indicator {
          margin-left: 0.25rem;
        }

        .form-group input:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .field-hint {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.5);
          overflow-wrap: anywhere;
        }

        .detect-location-button {
          padding: 0.75rem 1rem;
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.5);
          border-radius: 8px;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1.2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 48px;
          flex: 0 0 48px;
        }

        .detect-location-button:hover:not(:disabled) {
          background: rgba(139, 92, 246, 0.3);
          transform: translateY(-2px);
        }

        .detect-location-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .save-button, .cancel-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .save-button {
          background: #8b5cf6;
          border: none;
          color: #ffffff;
        }

        .save-button:hover {
          background: #7c3aed;
          transform: translateY(-2px);
        }

        .cancel-button {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: #ffffff;
        }

        .cancel-button:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        .profile-content {
          display: flex;
          flex-direction: column;
          gap: clamp(1rem, 1.6vw, 1.5rem);
        }

        .section {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          padding: clamp(1rem, 1.8vw, 1.5rem);
          min-width: 0;
        }

        .section h3 {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: clamp(1.15rem, 1vw + 0.85rem, 1.4rem);
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 1rem;
        }

        .numbers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
          gap: 1.5rem;
          min-width: 0;
        }

        .frequencies-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
          gap: 1rem;
          min-width: 0;
        }

        .frequency-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .frequency-card:hover {
          border-color: #8b5cf6;
          background: rgba(139, 92, 246, 0.1);
        }

        .freq-icon {
          color: #f59e0b;
        }

        .freq-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .freq-label {
          font-family: var(--pp-font-sans);
          font-size: var(--pp-text-label);
          color: rgba(255, 255, 255, 0.6);
          text-transform: uppercase;
          letter-spacing: var(--pp-tracking);
        }

        .freq-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #ffffff;
          font-family: var(--pp-font-mono);
        }

        .freq-description {
          font-family: var(--pp-font-sans);
          font-size: var(--pp-text-label);
          color: rgba(255, 255, 255, 0.5);
        }

        .play-button {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #8b5cf6;
          border: none;
          color: #ffffff;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .play-button:hover {
          background: #7c3aed;
          transform: scale(1.1);
        }

        .summary-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .summary-item strong {
          display: block;
          color: #8b5cf6;
          font-size: 1.05rem;
          margin-bottom: 0.5rem;
        }

        .summary-item p {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
        }

        .summary-item ul {
          list-style: none;
          padding: 0;
        }

        .summary-item li {
          color: rgba(255, 255, 255, 0.7);
          padding: 0.5rem 0;
          padding-left: 1.5rem;
          position: relative;
        }

        .summary-item li::before {
          content: '→';
          position: absolute;
          left: 0;
          color: #8b5cf6;
        }

        /* Archetype Card Styles */
        .archetype-card {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .archetype-card:hover {
          border-color: #8b5cf6;
          transform: translateY(-5px);
          box-shadow: 0 8px 32px rgba(139, 92, 246, 0.3);
        }

        .archetype-header {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: clamp(1rem, 1.5vw, 1.25rem);
          cursor: pointer;
          transition: background 0.3s ease;
          min-width: 0;
        }

        .archetype-header:hover {
          background: rgba(139, 92, 246, 0.05);
        }

        .archetype-icon {
          width: 48px;
          height: 48px;
          background: rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8b5cf6;
          flex-shrink: 0;
        }

        .archetype-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          min-width: 0;
        }

        .archetype-label {
          display: block;
          font-family: var(--pp-font-sans);
          font-size: var(--pp-text-label);
          color: rgba(255, 255, 255, 0.6);
          text-transform: uppercase;
          letter-spacing: var(--pp-tracking);
        }

        .archetype-value {
          display: block;
          font-size: 2.5rem;
          font-weight: 700;
          color: #8b5cf6;
          font-family: var(--pp-font-mono);
        }

        .archetype-title {
          display: block;
          font-size: 1.1rem;
          font-weight: 600;
          color: #ffffff;
          font-family: var(--pp-font-sans);
        }

        .archetype-essence {
          font-family: var(--pp-font-sans);
          font-size: var(--pp-text-body);
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.5;
          margin-top: 0.5rem;
          overflow-wrap: anywhere;
        }

        .expand-button {
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.3s ease;
        }

        .expand-button:hover {
          color: #8b5cf6;
        }

        .archetype-details {
          overflow: hidden;
          border-top: 1px solid rgba(139, 92, 246, 0.2);
        }

        .archetype-details-content {
          padding: clamp(1rem, 1.5vw, 1.25rem);
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .detail-section {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .detail-section h4 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1rem;
          font-weight: 600;
          color: #8b5cf6;
          margin: 0;
          font-family: var(--pp-font-sans);
        }

        .meaning-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .meaning-item strong {
          color: rgba(255, 255, 255, 0.9);
          font-size: var(--pp-text-body);
          font-family: var(--pp-font-sans);
        }

        .meaning-item p {
          color: rgba(255, 255, 255, 0.7);
          font-size: var(--pp-text-body);
          font-family: var(--pp-font-sans);
          line-height: 1.6;
          margin: 0;
          overflow-wrap: anywhere;
        }

        .meaning-item ul {
          list-style: none;
          padding: 0;
          margin: 0.5rem 0 0 0;
        }

        .meaning-item li {
          color: rgba(255, 255, 255, 0.7);
          font-size: var(--pp-text-body);
          font-family: var(--pp-font-sans);
          padding: 0.25rem 0;
          padding-left: 1.25rem;
          position: relative;
          overflow-wrap: anywhere;
        }

        .meaning-item li::before {
          content: '•';
          position: absolute;
          left: 0;
          color: #8b5cf6;
        }

        .traits-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .trait-column {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .trait-label {
          font-family: var(--pp-font-sans);
          font-size: var(--pp-text-label);
          text-transform: uppercase;
          letter-spacing: var(--pp-tracking);
          padding-bottom: 0.5rem;
          border-bottom: 1px solid rgba(139, 92, 246, 0.2);
        }

        .trait-label.positive {
          color: #10b981;
        }

        .trait-label.negative {
          color: #ef4444;
        }

        .trait-column ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .trait-column li {
          color: rgba(255, 255, 255, 0.7);
          font-size: var(--pp-text-meta);
          font-family: var(--pp-font-sans);
          padding: 0.25rem 0;
          padding-left: 1rem;
          position: relative;
        }

        .trait-column li::before {
          content: '→';
          position: absolute;
          left: 0;
          color: #8b5cf6;
          font-size: 0.75rem;
        }

        .sacred-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 0.75rem;
        }

        .sacred-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .sacred-item strong {
          color: rgba(255, 255, 255, 0.6);
          font-size: var(--pp-text-label);
          font-family: var(--pp-font-sans);
          text-transform: uppercase;
          letter-spacing: var(--pp-tracking);
        }

        .sacred-item span {
          color: rgba(255, 255, 255, 0.9);
          font-size: var(--pp-text-body);
          font-family: var(--pp-font-sans);
        }

        .tags-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }

        .tag {
          padding: 0.4rem 0.75rem;
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 6px;
          font-size: var(--pp-text-label);
          font-family: var(--pp-font-sans);
          color: rgba(255, 255, 255, 0.8);
        }

        .tag.small {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
        }

        .tag.positive {
          background: rgba(16, 185, 129, 0.2);
          border-color: rgba(16, 185, 129, 0.3);
          color: #10b981;
        }

        .tag.negative {
          background: rgba(239, 68, 68, 0.2);
          border-color: rgba(239, 68, 68, 0.3);
          color: #ef4444;
        }

        .relationship-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .relationship-column {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .cultural-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
          gap: 1rem;
        }

        .cultural-item {
          padding: 1rem;
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .cultural-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .cultural-header strong {
          color: #8b5cf6;
          font-size: 0.9rem;
        }

        .lucky-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
          font-family: var(--pp-font-sans);
          text-transform: uppercase;
          letter-spacing: var(--pp-tracking);
        }

        .lucky-badge.lucky {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .lucky-badge.unlucky {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .lucky-badge.neutral {
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .lucky-badge.sacred {
          background: rgba(139, 92, 246, 0.2);
          color: #8b5cf6;
          border: 1px solid rgba(139, 92, 246, 0.3);
        }

        .cultural-meaning {
          color: rgba(255, 255, 255, 0.8);
          font-size: var(--pp-text-meta);
          font-family: var(--pp-font-sans);
          line-height: 1.5;
          margin: 0;
        }

        .cultural-notes {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.75rem;
          font-family: var(--pp-font-sans);
          font-style: italic;
          margin: 0;
          margin-top: 0.25rem;
        }

        /* Shape Definitions Styles */
        .shape-definitions-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 0.75rem;
        }

        .shape-definition-item {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .shape-name {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          margin-bottom: 0.25rem;
        }

        .shape-name strong {
          color: #8b5cf6;
          font-size: 1rem;
          font-weight: 600;
        }

        .shape-geometric-form {
          color: rgba(139, 92, 246, 0.7);
          font-size: var(--pp-text-meta);
          font-family: var(--pp-font-mono);
          font-style: italic;
        }

        .shape-description {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }

        .shape-significance {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.85rem;
          line-height: 1.5;
          margin: 0;
          font-style: italic;
        }

        .shape-practical-section {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(139, 92, 246, 0.2);
        }

        .shape-section-title {
          display: block;
          color: #10b981;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          margin-top: 1rem;
        }

        .shape-practical-application {
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 0 0 1rem 0;
          padding: 0.75rem;
          background: rgba(16, 185, 129, 0.1);
          border-left: 3px solid #10b981;
          border-radius: 4px;
        }

        .shape-examples-section,
        .shape-actions-section {
          margin-top: 1rem;
        }

        .shape-examples-list,
        .shape-actions-list {
          list-style: none;
          padding: 0;
          margin: 0.5rem 0 0 0;
        }

        .shape-examples-list li,
        .shape-actions-list li {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.85rem;
          line-height: 1.6;
          padding: 0.5rem 0;
          padding-left: 1.5rem;
          position: relative;
        }

        .shape-examples-list li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #10b981;
          font-weight: bold;
        }

        .shape-actions-list li::before {
          content: '→';
          position: absolute;
          left: 0;
          color: #8b5cf6;
          font-weight: bold;
        }

        /* Astrological Chart Styles */
        .astrological-chart {
          margin-top: 2rem;
        }

        .big-three {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .big-three-item {
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
        }

        .big-three-label {
          font-size: var(--pp-text-meta);
          font-family: var(--pp-font-sans);
          color: rgba(255, 255, 255, 0.6);
          text-transform: uppercase;
          letter-spacing: var(--pp-tracking);
          margin-bottom: 0.5rem;
        }

        .big-three-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #8b5cf6;
          margin-bottom: 0.5rem;
          font-family: var(--pp-font-mono);
        }

        .big-three-description {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .planets-grid h4,
        .elemental-balance h4,
        .aspects-section h4,
        .houses-section h4 {
          font-size: 1.2rem;
          font-weight: 600;
          color: #8b5cf6;
          margin-bottom: 1rem;
          margin-top: 1.5rem;
        }

        .planets-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr));
          gap: 1rem;
        }

        .planet-item {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          padding: 1rem;
        }

        .planet-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .planet-name {
          font-weight: 600;
          color: #ffffff;
        }

        .planet-sign {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.8);
        }

        .planet-details {
          display: flex;
          gap: 0.75rem;
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: 0.75rem;
        }

        .planet-element,
        .planet-modality,
        .planet-house {
          padding: 0.25rem 0.5rem;
          background: rgba(139, 92, 246, 0.2);
          border-radius: 4px;
        }

        .planet-interpretation {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid rgba(139, 92, 246, 0.1);
        }

        .planet-meaning {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.5;
          margin: 0 0 0.5rem 0;
        }

        .planet-house-meaning {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.6);
          font-style: italic;
          margin: 0;
        }

        .elements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
          gap: 1rem;
        }

        .element-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .element-label {
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
        }

        .element-bar {
          width: 100%;
          height: 8px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 4px;
          overflow: hidden;
        }

        .element-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .element-fill.fire {
          background: linear-gradient(90deg, #ef4444, #f59e0b);
        }

        .element-fill.earth {
          background: linear-gradient(90deg, #84cc16, #65a30d);
        }

        .element-fill.air {
          background: linear-gradient(90deg, #3b82f6, #2563eb);
        }

        .element-fill.water {
          background: linear-gradient(90deg, #06b6d4, #0891b2);
        }

        .element-count {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .aspects-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr));
          gap: 0.75rem;
        }

        .aspect-item {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          padding: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .aspect-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .aspect-planets {
          font-weight: 600;
          color: #ffffff;
          font-size: 0.9rem;
        }

        .aspect-details {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .aspect-meaning {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.5;
          margin: 0;
        }

        .houses-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 140px), 1fr));
          gap: 0.75rem;
        }

        .house-item {
          background: rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.2);
          border-radius: 8px;
          padding: 0.75rem;
          text-align: center;
        }

        .house-number {
          font-weight: 600;
          color: #8b5cf6;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }

        .house-sign {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 0.5rem;
        }

        .house-meaning {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.4;
        }

        @media (max-width: 768px) {
          .profile-header {
            flex-direction: column;
            gap: 0.75rem;
          }

          .profile-header-actions {
            width: 100%;
            justify-content: stretch;
          }

          .view-toggle-button,
          .profile-header-actions .edit-button {
            width: 100%;
            justify-content: center;
          }

          .personal-profile-container {
            padding: 0.75rem;
          }

          .section {
            padding: 1rem;
          }

          .city-input-row {
            flex-wrap: wrap;
          }

          .city-input-row > div:first-child {
            flex-basis: 100%;
          }

          .detect-location-button {
            width: 100%;
            flex: 1 1 100%;
            min-width: 0;
          }

          .coordinates-grid {
            grid-template-columns: 1fr !important;
          }

          .form-actions {
            flex-direction: column;
          }

          .save-button,
          .cancel-button {
            width: 100%;
            justify-content: center;
          }

          .traits-grid,
          .relationship-grid {
            grid-template-columns: 1fr;
          }

          .cultural-grid {
            grid-template-columns: 1fr;
          }

          .big-three {
            grid-template-columns: 1fr;
          }

          .planets-list,
          .elements-grid,
          .aspects-list,
          .houses-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default PersonalProfile;
